import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Container, Typography, Paper, CircularProgress, ToggleButton, ToggleButtonGroup, Slider, TextField, Stack, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel } from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTestPaymentMutation } from '@/services/api';
import PaidIcon from '@mui/icons-material/Paid';
import DescriptionIcon from '@mui/icons-material/Description';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import InsightsIcon from '@mui/icons-material/Insights';
import { useSnackbar } from 'notistack';

const TR_TYPES = [
  { value: 'Lumpsum', label: 'Lumpsum', icon: <PaidIcon fontSize="small" /> },
  { value: 'Emandate', label: 'Emandate', icon: <DescriptionIcon fontSize="small" /> },
];

const PAY_MODES = [
  { value: 'NetBanking', label: 'NetBanking', icon: <AccountBalanceIcon fontSize="small" /> },
  { value: 'Debit Card', label: 'Debit Card', icon: <CreditCardIcon fontSize="small" /> },
  { value: 'UPI', label: 'UPI', icon: <QrCode2Icon fontSize="small" /> },
  { value: 'OTM', label: 'OTM', icon: <AutorenewIcon fontSize="small" /> },
  { value: 'UPIAutopay', label: 'UPIAutopay', icon: <RocketLaunchIcon fontSize="small" /> },
  { value: 'Enach', label: 'ENACH', icon: <DescriptionIcon fontSize="small" /> },
  { value: 'ISIP', label: 'ISIP', icon: <InsightsIcon fontSize="small" /> },
];

const AUTH_MODES = [
  { value: 'DCB', label: 'Net Banking', icon: <AccountBalanceIcon fontSize="small" /> },
  { value: 'DC', label: 'Debit Card', icon: <CreditCardIcon fontSize="small" /> },
  { value: 'UPI', label: 'UPI', icon: <QrCode2Icon fontSize="small" /> },
  { value: 'AADHAR', label: 'Aadhar', icon: <DescriptionIcon fontSize="small" /> },
  { value: 'PAN', label: 'PAN', icon: <DescriptionIcon fontSize="small" /> },
];

//util func 
function filterModes(dictList: any[], allowedValues: string[] | undefined) {
  // If no filtering OR empty array → return full list
  if (!allowedValues || allowedValues.length === 0) return dictList;

  return dictList.filter(item => allowedValues.map(v => v.toLowerCase()).includes(item.label.toLowerCase()));
}

//helper func 
function getConfigs(data: any, isDynamic: boolean) {
  const trType = new Set<string>();
  const payMode = new Set<string>();
  const authMode = new Set<string>();
  if (isDynamic) {
    const config = JSON.parse(data);
    const rules = config.rules || [];

    const extractFromConditions : any = (conditions: any) => {
      if (!conditions) return;

      // transactionType → TR type
      if (conditions.transactionType?.value) {
        trType.add(conditions.transactionType.value.toLowerCase());
      }
      /**  -------> FOR DYNAMIC WE HAD FALL BACK (it taks all possible edgeCases) <--------- 
       * so lets have the fallback for paymentMode and authorizationMode as all possible values
      */
      // if (conditions.paymentMode?.value) {
      //   payMode.add(conditions.paymentMode.value.toLowerCase());
      // }
      // if (conditions.authorizationMode?.value) {
      //   authMode.add(conditions.authorizationMode.value.toLowerCase());
      // }
    };

    const walkRules : any = (ruleList: any[]) => {
      ruleList.forEach(rule => {
        extractFromConditions(rule.conditions);

        if (rule.subRules?.length) {
          walkRules(rule.subRules);
        }
      });
    };

    walkRules(rules);

    return {
      allowedTRType: Array.from(trType),
      allowedPayMode: Array.from(payMode),
      allowedAuthMode: Array.from(authMode),
    };
  } else {
    const validConfigs = data.filter((config: any) => {
      try { return config.merchantConfig && JSON.parse(config.merchantConfig).length }
      catch { return false }
    });
    validConfigs.forEach((cfg: any) => {
      if (cfg.trtype) trType.add(cfg.trtype);
      if (cfg.paymode) payMode.add(cfg.paymode);
      if (cfg.authmode) authMode.add(cfg.authmode);
    });
    return { allowedTRType: Array.from(trType), allowedPayMode: Array.from(payMode), allowedAuthMode: Array.from(authMode) };
  }
}

export default function SampleTransaction() {
  const { data, isDynamic } = useLocation().state;
  const {allowedTRType, allowedPayMode, allowedAuthMode } = getConfigs(data, isDynamic);
  const allowedTR = filterModes(TR_TYPES, allowedTRType);
  const allowedPM = filterModes(PAY_MODES, allowedPayMode);
  const allowedAM = filterModes(AUTH_MODES, allowedAuthMode);
  const { enqueueSnackbar } = useSnackbar();
  const { assetId = '' } = useParams<{ id: string; assetId: string }>();
  const navigate = useNavigate();

  const [trType, setTrType] = useState<string>(allowedTR[0].value);
  const [payMode, setPayMode] = useState<string>(allowedPM[0].value);
  const [authMode, setAuthMode] = useState<string>(allowedAM[0].value);
  const [upiMode, setUpiMode] = useState<string>('Collect');
  const [generatedUrl, setGeneratedUrl] = useState<string>('');
  const [openLoading, setOpenLoading] = useState<boolean>(false);
  const openAbortRef = useRef<AbortController | null>(null);

  // Optional bank override fields (to be sent at top-level if provided)
  const [bankDialogOpen, setBankDialogOpen] = useState(false);
  const [bankOverrides, setBankOverrides] = useState<null | {
    accountNumber?: string;
    ifscCode?: string;
    bankName?: string;
    upi?: string;
  }>(null);
  const [bankForm, setBankForm] = useState({
    accountNumber: '',
    ifscCode: '',
    bankName: '',
    upi: '',
  });
  const [bankErrors, setBankErrors] = useState<{ accountNumber?: string; ifscCode?: string; bankName?: string; upi?: string }>({});
  const [useBankOverrides, setUseBankOverrides] = useState(false);
  const [saveLocally, setSaveLocally] = useState(false);

  const LS_KEY = 'pp.sampleTr.bankOverrides';

  useEffect(() => {
    // Load any saved overrides from localStorage on mount
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          setBankOverrides(parsed);
          // do not auto-enable usage; let user opt-in via checkbox
        }
      }
    } catch {
      // ignore JSON or access errors
    }
  }, []);

  const validateBankForm = () => {
    const errs: { accountNumber?: string; ifscCode?: string; bankName?: string; upi?: string } = {};
    const hasVal = (v?: string) => !!(v && v.trim().length);
    if (hasVal(bankForm.accountNumber)) {
      const acct = bankForm.accountNumber.replace(/\s+/g, '');
      if (!/^\d{6,18}$/.test(acct)) {
        errs.accountNumber = 'Account number should be 6-18 digits';
      }
    }
    if (hasVal(bankForm.ifscCode)) {
      const ifsc = bankForm.ifscCode.toUpperCase();
      if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) {
        errs.ifscCode = 'Invalid IFSC format (e.g., HDFC0001234)';
      }
    }
    if (hasVal(bankForm.bankName)) {
      if (bankForm.bankName.trim().length < 2) {
        errs.bankName = 'Bank name is too short';
      }
    }
    if (hasVal(bankForm.upi)) {
      const upi = bankForm.upi.toLowerCase();
      if (!/^[a-z0-9._\-]{2,}@[a-z]{2,}(\.[a-z]{2,})?$/.test(upi)) {
        errs.upi = 'Invalid UPI ID format (e.g., name@bank)';
      }
    }
    setBankErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Continuous logarithmic amount slider setup
  const AMOUNT_MIN = 1;
  const AMOUNT_MAX = 1_000_000;
  const ratio = useMemo(() => Math.log(AMOUNT_MAX / AMOUNT_MIN), []);
  const amountFromSlider = (sv: number) => {
    const val = AMOUNT_MIN * Math.exp((sv / 100) * ratio);
    return Math.round(val);
  };

  const handleOpenLink = async () => {
    try {
      setOpenLoading(true);
      const controller = new AbortController();
      openAbortRef.current = controller;
      const url = new URL(generatedUrl);
      const callbackUrl = new URL(location.href);
      callbackUrl.pathname = '/payments/payments/callback';
      // Attach returnTo so callback page knows where to navigate back
      const returnTo = window.location.pathname + window.location.search;
      callbackUrl.searchParams.set('returnTo', returnTo);
      url.searchParams.set('callbackSuccessUrl', callbackUrl.toString());
      url.searchParams.set('callbackFailureUrl', callbackUrl.toString());
      const res = await fetch(url.toString(), { method: 'GET', signal: controller.signal });
      const data = await res.json();
      if (data?.url) {
        console.log("Launching " + data.url);
        (window as any).location.href = data.url;
      } else {
        throw new Error(data.message);
      }
    } catch (e: any) {
      if (e?.name === 'AbortError') {
        enqueueSnackbar('Request cancelled', { variant: 'info' });
      } else {
        enqueueSnackbar('Failed to fetch redirect URL. ' + e.message, { variant: 'error' });
      }
    } finally {
      setOpenLoading(false);
      openAbortRef.current = null;
      setGeneratedUrl('');
    }
  };

  const handleCancelOpen = () => {
    if (openAbortRef.current) {
      openAbortRef.current.abort();
    }
    setGeneratedUrl('');
  };
  const sliderFromAmount = (amt: number) => {
    const clamped = Math.min(Math.max(amt, AMOUNT_MIN), AMOUNT_MAX);
    return (Math.log(clamped / AMOUNT_MIN) / ratio) * 100;
  };
  const amountMarks = useMemo(
    () => [1, 10, 100, 1_000, 10_000, 100_000, 1_000_000].map((a) => ({
      value: sliderFromAmount(a),
      label: a.toLocaleString('en-IN'),
    })),
    [ratio]
  );
  const [amount, setAmount] = useState<number>(1000);
  const [sliderVal, setSliderVal] = useState<number>(() => sliderFromAmount(1000));
  const [isRta, setIsRta] = useState(false);
  const [fundCode, setFundCode] = useState('');
  const showUpiMode = (payMode === 'UPI' || payMode === 'UPIAutopay') && authMode === 'UPI';

  const [testPayment, { isLoading }] = useTestPaymentMutation();

  const handleGenerate = async () => {
    try {
      const payload: any = { assetId, trType, payMode, authMode, amount };
      if (showUpiMode && upiMode) {
        payload.upiMode = upiMode;
      }
      const hasOverrides = bankOverrides && Object.values(bankOverrides).some((v) => !!(v && String(v).trim().length));
      if (useBankOverrides && hasOverrides) {
        Object.assign(payload, bankOverrides);
      }
      if (isRta && fundCode.trim()) {
        payload.fundCode = fundCode.trim();
      }
      const { url } = await testPayment(payload).unwrap();
      if (url) setGeneratedUrl(url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to generate test payment link', e);
      enqueueSnackbar("Failed to navigate to payment " + (e as Error).message)
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Button variant="text" onClick={() => navigate(-1)}>← Go back</Button>
      </Box>

      <Typography variant="h5" fontWeight={700} gutterBottom>
        Create Sample Transaction
      </Typography>

      <Paper sx={{ p: 3 }}>
        <Box display="flex" flexDirection="column" gap={3}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Transaction Type</Typography>
            <ToggleButtonGroup
              color="primary"
              exclusive
              value={trType}
              onChange={(_, val) => val && setTrType(val)}
            >
              {allowedTR.map((o) => (
                <ToggleButton key={o.value} value={o.value} sx={{ px: 2, py: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {o.icon}
                    <span>{o.label}</span>
                  </Stack>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Payment Mode</Typography>
            <ToggleButtonGroup
              color="primary"
              exclusive
              value={payMode}
              onChange={(_, val) => val && setPayMode(val)}
            >
              {allowedPM.map((o) => (
                <ToggleButton key={o.value} value={o.value} sx={{ px: 2, py: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {o.icon}
                    <span>{o.label}</span>
                  </Stack>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Authorization Mode</Typography>
            <ToggleButtonGroup
              color="primary"
              exclusive
              value={authMode}
              onChange={(_, val) => val && setAuthMode(val)}
            >
              {allowedAM.map((o) => (
                <ToggleButton key={o.value} value={o.value} sx={{ px: 2, py: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {o.icon}
                    <span>{o.label}</span>
                  </Stack>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {showUpiMode && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>UPI Mode</Typography>
              <ToggleButtonGroup
                color="primary"
                exclusive
                value={upiMode}
                onChange={(_, val) => val && setUpiMode(val)}
              >
                <ToggleButton value="COLLECT" sx={{ px: 2, py: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <span>Collect</span>
                  </Stack>
                </ToggleButton>
                <ToggleButton value="INTENT" sx={{ px: 2, py: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <span>Intent</span>
                  </Stack>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          <Box>
            <FormControlLabel
              control={<Checkbox checked={isRta} onChange={(e) => setIsRta(e.target.checked)} />}
              label="Is RTA"
            />
            {isRta && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Fund Code</Typography>
                <TextField
                  value={fundCode}
                  onChange={(e) => setFundCode(e.target.value)}
                  size="small"
                  sx={{ width: 170 }}
                  placeholder="Enter fund code"
                />
              </Box>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Amount</Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Slider
                value={sliderVal}
                min={0}
                max={100}
                step={0.1}
                marks={amountMarks}
                onChange={(_, sv) => {
                  const v = Array.isArray(sv) ? sv[0] : sv;
                  setSliderVal(v);
                  setAmount(amountFromSlider(v));
                }}
                sx={{ flexGrow: 1 }}
              />
              <TextField
                label="Exactly"
                size="small"
                value={amount}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^0-9]/g, '');
                  const v = Number(raw);
                  if (Number.isNaN(v)) return;
                  const clamped = Math.min(Math.max(v, AMOUNT_MIN), AMOUNT_MAX);
                  setAmount(clamped);
                  setSliderVal(sliderFromAmount(clamped));
                }}
                InputProps={{
                  startAdornment: <span style={{ marginRight: 4 }}>₹</span>,
                }}
                sx={{ width: 170 }}
              />
            </Stack>
          </Box>

          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <Stack direction="row" spacing={1} alignItems="center">
              <FormControlLabel
                control={<Checkbox checked={useBankOverrides} onChange={(e) => setUseBankOverrides(e.target.checked)} disabled={!bankOverrides} />}
                label={<>
                  <Button variant="text" sx={{ textTransform: 'none' }} onClick={() => {
                    // preload form with saved details if any
                    setBankForm({
                      accountNumber: bankOverrides?.accountNumber || '',
                      ifscCode: bankOverrides?.ifscCode || '',
                      bankName: bankOverrides?.bankName || '',
                      upi: bankOverrides?.upi || '',
                    });
                    // initialize save locally checkbox based on existing LS value
                    try {
                      setSaveLocally(!!localStorage.getItem(LS_KEY));
                    } catch {
                      setSaveLocally(false);
                    }
                    setBankDialogOpen(true);
                  }}>
                    {bankOverrides ? 'Use updated Bank Details' : 'Update Bank Details'}
                  </Button>
                </>}
              />
            </Stack>
          </Box>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            {!generatedUrl && (
              <Button variant="contained" onClick={handleGenerate} disabled={isLoading}>
                {isLoading ? (<><CircularProgress size={18} sx={{ mr: 1, textTransform: 'none' }} /> Generating...</>) : 'Generate Link'}
              </Button>)}
            {generatedUrl && (
              <>
                <Button variant="contained" onClick={handleOpenLink} disabled={openLoading}>
                  {openLoading ? (<><CircularProgress size={18} sx={{ mr: 1, textTransform: 'none' }} /> Launching...</>) : 'Launch Transaction'}
                </Button>
                {openLoading && (
                  <Button variant="text" color="inherit" onClick={handleCancelOpen}>Cancel</Button>
                )}
              </>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Bank Details Dialog */}
      <Dialog open={bankDialogOpen} onClose={() => setBankDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Update Bank Details (Optional)</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Account Number"
              value={bankForm.accountNumber}
              onChange={(e) => setBankForm((s) => ({ ...s, accountNumber: e.target.value }))}
              fullWidth
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              error={!!bankErrors.accountNumber}
              helperText={bankErrors.accountNumber}
            />
            <TextField
              label="IFSC Code"
              value={bankForm.ifscCode}
              onChange={(e) => setBankForm((s) => ({ ...s, ifscCode: e.target.value.toUpperCase() }))}
              fullWidth
              error={!!bankErrors.ifscCode}
              helperText={bankErrors.ifscCode}
            />
            <TextField
              label="Bank Name"
              value={bankForm.bankName}
              onChange={(e) => setBankForm((s) => ({ ...s, bankName: e.target.value }))}
              fullWidth
              error={!!bankErrors.bankName}
              helperText={bankErrors.bankName}
            />
            <TextField
              label="UPI ID"
              value={bankForm.upi}
              onChange={(e) => setBankForm((s) => ({ ...s, upi: e.target.value.toLowerCase() }))}
              fullWidth
              error={!!bankErrors.upi}
              helperText={bankErrors.upi}
            />
            <FormControlLabel
              control={<Checkbox checked={saveLocally} onChange={(e) => setSaveLocally(e.target.checked)} />}
              label="Save Locally"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBankDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => {
            const anyVal = Object.values(bankForm).some((v) => !!(v && String(v).trim().length));
            if (!anyVal) {
              setBankOverrides(null);
              try {
                localStorage.removeItem(LS_KEY);
              } catch { }
              setBankDialogOpen(false);
              enqueueSnackbar('Cleared bank details', { variant: 'info' });
              return;
            }
            if (!validateBankForm()) return;
            setBankOverrides(bankForm);
            // persist or clear based on saveLocally
            try {
              if (saveLocally) {
                localStorage.setItem(LS_KEY, JSON.stringify(bankForm));
              } else {
                localStorage.removeItem(LS_KEY);
              }
            } catch { }
            setBankDialogOpen(false);
            enqueueSnackbar('Bank details saved for this transaction', { variant: 'success' });
          }}>Save</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}


